import { useEffect, useState } from "react";
import { addMinutes, isoNoMs } from "@/lib/format";

/**
 * Owns drag-to-move (with "drop after another row" snap) and drag-to-resize for
 * schedule blocks. Returns reactive state plus the callbacks the grid wires up
 * on each row, so the grid component itself stays presentational.
 */
export function useBlockDrag({ blocks, onPatchBlock }) {
  // {blockId, startY, origStart, origEnd, deltaMin, mouseY, anchorBlockId, anchorEndAt, newStartAt}
  const [dragState, setDragState] = useState(null);
  // {blockId, startY, origEnd, origStart, deltaMin, mouseY}
  const [resizeState, setResizeState] = useState(null);

  const beginRowDrag = (block, e) => {
    if (block.is_locked) return;
    e.preventDefault();
    setDragState({
      blockId: block.id,
      startY: e.clientY,
      origStart: block.start_at,
      origEnd: block.end_at,
      deltaMin: 0,
      mouseY: e.clientY,
      anchorBlockId: null,
      anchorEndAt: null,
      newStartAt: null,
    });
  };

  const beginResize = (block, e) => {
    if (block.is_locked) return;
    e.preventDefault();
    e.stopPropagation();
    setResizeState({
      blockId: block.id,
      startY: e.clientY,
      origEnd: block.end_at,
      origStart: block.start_at,
      deltaMin: 0,
      mouseY: e.clientY,
    });
  };

  useEffect(() => {
    if (!dragState && !resizeState) return;

    // Given the mouse position, find which block row is under the cursor (the
    // "drop after" anchor). Inspects elements at the point for our row-{id}-{cell}
    // testid pattern.
    const findAnchor = (clientX, clientY) => {
      const els = document.elementsFromPoint(clientX, clientY);
      for (const el of els) {
        const tid = el.getAttribute?.("data-testid");
        if (!tid || !tid.startsWith("row-")) continue;
        const parts = tid.split("-");
        if (parts.length < 3) continue;
        const blockId = parts.slice(1, 6).join("-"); // UUIDs have 5 dash-separated segments
        const candidate = blocks.find((b) => b.id === blockId);
        if (candidate) return candidate;
      }
      return null;
    };

    const onMove = (e) => {
      if (dragState) {
        const anchor = findAnchor(e.clientX, e.clientY);
        let newStartAt = null;
        let anchorBlockId = null;
        let deltaMin = 0;
        if (anchor && anchor.id !== dragState.blockId) {
          newStartAt = anchor.end_at;
          anchorBlockId = anchor.id;
          deltaMin = Math.round((new Date(newStartAt) - new Date(dragState.origStart)) / 60000);
        }
        setDragState((s) =>
          s ? { ...s, mouseY: e.clientY, anchorBlockId, anchorEndAt: newStartAt, newStartAt, deltaMin } : s
        );
      }
      if (resizeState) {
        const deltaMin = Math.round((e.clientY - resizeState.startY) / 15) * 15;
        setResizeState((s) => (s ? { ...s, deltaMin, mouseY: e.clientY } : s));
      }
    };

    const onUp = async () => {
      if (dragState && dragState.newStartAt) {
        const durMs = new Date(dragState.origEnd) - new Date(dragState.origStart);
        const newStart = dragState.newStartAt;
        const newEnd = isoNoMs(new Date(new Date(newStart).getTime() + durMs));
        await onPatchBlock(dragState.blockId, { start_at: newStart, end_at: newEnd });
      }
      if (resizeState && resizeState.deltaMin !== 0) {
        const newEnd = isoNoMs(addMinutes(resizeState.origEnd, resizeState.deltaMin));
        if (new Date(newEnd) > new Date(resizeState.origStart)) {
          await onPatchBlock(resizeState.blockId, { end_at: newEnd });
        }
      }
      setDragState(null);
      setResizeState(null);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragState, resizeState, blocks, onPatchBlock]);

  return { dragState, resizeState, beginRowDrag, beginResize };
}
